import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import {
  RuntimeTemplateConfig,
  TemplateVersion,
  TemplateVersionControl,
} from '../../models/template';
import { ViewBridgeInspectionComponent } from '../../pages/inspections/view-bridge-inspection/view-bridge-inspection.component';
import { ViewTunnelInspectionComponent } from '../../pages/inspections/view-tunnel-inspection/view-tunnel-inspection.component';
import { BridgeTemplateVersion } from '../../shared/configs/bridge-template-version.config';
import { StructureType } from '../../shared/configs/structure-type.config';
import { TunnelTemplateVersion } from '../../shared/configs/tunnel-template-version.config';
import { InspectionService } from '../api/inspection/inspection.service';
import { InspectionCrudService } from '../api/inspection-crud/inspection-crud.service';
import { CubeService } from '../cube/cube.service';
import { LoaderService } from '../loader/loader.service';
import { IdbService } from '../storage/idb.service';
import { StorageService } from '../storage/storage.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class TemplateLoaderService {
  private runtimeTemplateConfig?: RuntimeTemplateConfig;

  constructor(
    private activatedRoute: ActivatedRoute,
    private cubeService: CubeService,
    private httpClient: HttpClient,
    private idbService: IdbService,
    private inspectionCrudService: InspectionCrudService,
    private inspectionService: InspectionService,
    private loaderService: LoaderService,
    private location: Location,
    private matDialog: MatDialog,
    private router: Router,
    private storageService: StorageService,
    private userService: UserService
  ) {}

  /**
   * Get available template versions for a given structure type.
   *
   * @param structureType - The structure type (bridge or tunnel)
   * @returns Array of available template versions, sorted in reverse order (newest first)
   */
  getAvailableVersions(structureType: string): TemplateVersion[] {
    const availableVersions: TemplateVersion[] = [];

    switch (structureType) {
      case StructureType.bridge:
        availableVersions.push(...Object.values(BridgeTemplateVersion));

        break;

      case StructureType.tunnel:
        availableVersions.push(...Object.values(TunnelTemplateVersion));

        break;
    }

    // Apply environment-based filtering if configured
    const filteredVersions = this.filterTemplateVersionsByEnv(
      structureType,
      availableVersions
    );

    // Reversing the array to get the latest version first
    return filteredVersions.reverse();
  }

  /**
   * Load and return a bridge template for the specified version.
   * Extracts the template and updates its version metadata.
   *
   * @param version - The version of the bridge template to load
   * @param templateFuncArgs - Optional arguments for the template function (component, isDisabled, etc.)
   * @returns Promise resolving to the loaded bridge template
   * @throws Error if template loading fails
   */
  async loadBridgeTemplate(
    version: BridgeTemplateVersion,
    templateFuncArgs: Record<string, any> = {}
  ): Promise<any> {
    try {
      // Get template import function
      const templateImportFn = this.getBridgeTemplateImportFn(version);

      // Extract template
      const template = await this.extractBridgeTemplate(
        templateImportFn,
        templateFuncArgs
      );

      // Update template version to the actual version
      return this.updateBridgeTemplateVersion(template, version);
    } catch (error) {
      console.error(`Failed to load bridge template v${version}:`, error);
      throw error;
    }
  }

  // Never called in the portal but can be used in the future if tunnel templates are added
  loadRuntimeTemplateConfig(): void {
    if (environment.devMode.loadRuntimeTemplateConfig) {
      console.log('Loading runtime template config...');

      this.httpClient
        .get<RuntimeTemplateConfig>('assets/runtime-template-config.json')
        .subscribe((data) => {
          this.runtimeTemplateConfig = data;
        });
    }
  }

  /**
   * Load and return a tunnel template for the specified version.
   * Extracts the template and updates its version metadata.
   *
   * @param version - The version of the tunnel template to load
   * @param templateFuncArgs - Optional arguments for the template function (component, isDisabled, userRole, etc.)
   * @returns Promise resolving to the loaded tunnel template
   * @throws Error if template loading fails
   */
  async loadTunnelTemplate(
    version: TunnelTemplateVersion,
    templateFuncArgs: Record<string, any> = {}
  ): Promise<any> {
    try {
      // Get template import function
      const templateImportFn = this.getTunnelTemplateImportFn(version);

      // Extract template
      const template = await this.extractTunnelTemplate(
        templateImportFn,
        templateFuncArgs
      );

      // Update template version to the actual version
      return this.updateTunnelTemplateVersion(template, version);
    } catch (error) {
      console.error(`Failed to load tunnel template v${version}:`, error);
      throw error;
    }
  }

  /**
   * Create a default instance of ViewBridgeInspectionComponent with all required dependencies.
   * Used as a fallback when no custom component is provided.
   *
   * @returns New instance of ViewBridgeInspectionComponent
   */
  private createDefaultBridgeTemplateComponent(): ViewBridgeInspectionComponent {
    return new ViewBridgeInspectionComponent(
      this.activatedRoute,
      this.cubeService,
      this.idbService,
      this.inspectionCrudService,
      this.inspectionService,
      this.loaderService,
      this.location,
      this.matDialog,
      this.router,
      this.storageService,
      this,
      this.userService
    );
  }

  /**
   * Create a default instance of ViewTunnelInspectionComponent with all required dependencies.
   * Used as a fallback when no custom component is provided.
   *
   * @returns New instance of ViewTunnelInspectionComponent
   */
  private createDefaultTunnelTemplateComponent(): ViewTunnelInspectionComponent {
    return new ViewTunnelInspectionComponent(
      this.activatedRoute,
      this.cubeService,
      this.idbService,
      this.inspectionCrudService,
      this.inspectionService,
      this.loaderService,
      this.location,
      this.router,
      this.storageService,
      this,
      this.userService
    );
  }

  /**
   * Extract bridge template by executing the import function and calling the template factory.
   * Applies custom component and disabled state if provided.
   *
   * @param templateImportFn - Function that returns a promise resolving to the module containing BridgeInputTemplate
   * @param templateFuncArgs - Optional arguments including component instance and disabled state
   * @returns Promise resolving to the extracted bridge template array
   */
  private async extractBridgeTemplate(
    templateImportFn: () => Promise<any>,
    templateFuncArgs: Record<string, any> = {}
  ): Promise<any[]> {
    const { BridgeInputTemplate } = await templateImportFn();

    const {
      component = this.createDefaultBridgeTemplateComponent(),
      isDisabled = false,
    } = templateFuncArgs;

    return BridgeInputTemplate(component, isDisabled);
  }

  /**
   * Extract tunnel template by executing the import function and calling the template factory.
   * Applies custom component, disabled state, and user role if provided.
   *
   * @param templateImportFn - Function that returns a promise resolving to the module containing TunnelInputTemplate
   * @param templateFuncArgs - Optional arguments including component instance, disabled state, and user role
   * @returns Promise resolving to the extracted tunnel template array
   */
  private async extractTunnelTemplate(
    templateImportFn: () => Promise<any>,
    templateFuncArgs: Record<string, any> = {}
  ): Promise<any[]> {
    const { TunnelInputTemplate } = await templateImportFn();

    const {
      component = this.createDefaultTunnelTemplateComponent(),
      isDisabled = false,
      userRole = this.userService.getActiveRole(),
    } = templateFuncArgs;

    return TunnelInputTemplate(component, isDisabled, userRole);
  }

  /**
   * Apply template filters (whitelist and blacklist) to the available versions.
   * If no template control config is specified, return all available versions.
   * If whitelist exists, only versions in whitelist are included.
   * If blacklist exists, versions in blacklist are excluded.
   * If both exist, whitelist is applied first, then blacklist.
   *
   * @param structureType - The structure type (bridge or tunnel)
   * @param availableVersions - Array of all available template versions
   * @returns Filtered array of template versions
   */
  private filterTemplateVersionsByEnv(
    structureType: string,
    availableVersions: TemplateVersion[]
  ): TemplateVersion[] {
    const versionControlObj: TemplateVersionControl =
      this.runtimeTemplateConfig[structureType];

    // If no template control config is specified, return an empty array
    if (!versionControlObj) {
      console.warn(
        `No template version control config found for structure type: ${structureType}`
      );

      return [];
    }

    const { whitelist = [], blacklist = [] } = versionControlObj;
    if (!whitelist.length) {
      // Only whitelist is mandatory (atleast one version or "*.*.*" is required), so if no whitelist is specified, return an empty array
      console.warn(
        `No template versions whitelisted for structure type: ${structureType}`
      );

      return [];
    }

    let filteredVersions: TemplateVersion[];
    // Apply whitelist to all available versions
    if (whitelist.length > 0) {
      filteredVersions = availableVersions.filter((version) =>
        // Include versions that match any pattern in whitelist
        whitelist.some((pattern) => this.isPatternMatch(version, pattern))
      );
    }

    // Apply blacklist to whitelisted versions
    if (blacklist.length > 0) {
      filteredVersions = filteredVersions.filter(
        (version) =>
          // Exclude versions that match any pattern in blacklist
          !blacklist.some((pattern) => this.isPatternMatch(version, pattern))
      );
    }

    return filteredVersions;
  }

  /**
   * Get the import function for a specific bridge template version.
   * Returns local template in dev mode, otherwise resolves to the appropriate version.
   * Groups similar versions together to avoid maintaining multiple templates.
   *
   * @param version - The bridge template version
   * @returns Function that returns a promise resolving to the template module
   */
  private getBridgeTemplateImportFn(
    version: BridgeTemplateVersion
  ): () => Promise<any> {
    // TEMP: For development
    if (environment.devMode.loadLocalInputTemplate) {
      return () =>
        import(
          /* webpackChunkName: "bridge-/vlocal" */
          '../../shared/templates/bridge/local-input-template'
        );
    }

    // Reusing the same template for multiple versions which are same to avoid maintaining multiple templates
    switch (version) {
      case BridgeTemplateVersion.V6_12_0:
      case BridgeTemplateVersion.V6_13_0:
      case BridgeTemplateVersion.V6_13_2:
        return () =>
          import(
            /* webpackChunkName: "bridge-/v6.13.2" */
            '../../shared/templates/bridge/template-archive/v6.13.2'
          );

      case BridgeTemplateVersion.V7_0_0:
      case BridgeTemplateVersion.V7_1_0:
      case BridgeTemplateVersion.V7_1_2:
      case BridgeTemplateVersion.V7_1_3:
      case BridgeTemplateVersion.V7_1_4:
        return () =>
          import(
            /* webpackChunkName: "bridge-/v7.1.4" */
            '../../shared/templates/bridge/template-archive/v7.1.4'
          );

      case BridgeTemplateVersion.V7_2_0:
      case BridgeTemplateVersion.V7_2_2:
      case BridgeTemplateVersion.V7_2_3:
      case BridgeTemplateVersion.V7_2_4:
      case BridgeTemplateVersion.V7_2_5:
      case BridgeTemplateVersion.V8_0_0:
      case BridgeTemplateVersion.V8_0_1:
      case BridgeTemplateVersion.V8_1_0:
      case BridgeTemplateVersion.V8_1_1:
      case BridgeTemplateVersion.V8_2_0:
      case BridgeTemplateVersion.V8_2_1:
      case BridgeTemplateVersion.V8_2_2:
      case BridgeTemplateVersion.V8_3_0:
      case BridgeTemplateVersion.V8_4_0:
      case BridgeTemplateVersion.V8_4_1:
      case BridgeTemplateVersion.V8_4_2:
      case BridgeTemplateVersion.V8_5_0:
      case BridgeTemplateVersion.V8_5_1:
      case BridgeTemplateVersion.V8_5_2:
      case BridgeTemplateVersion.V9_0_0:
      case BridgeTemplateVersion.V10_0_0:
      case BridgeTemplateVersion.V10_1_0:
        return () =>
          import(
            /* webpackChunkName: "bridge-/v10.1.0" */
            '../../shared/templates/bridge/template-archive/v10.1.0'
          );
    }
  }

  /**
   * Get the import function for a specific tunnel template version.
   * Returns local template in dev mode, otherwise resolves to the appropriate version.
   * Groups similar versions together to avoid maintaining multiple templates.
   *
   * @param version - The tunnel template version
   * @returns Function that returns a promise resolving to the template module
   */
  private getTunnelTemplateImportFn(
    version: TunnelTemplateVersion
  ): () => Promise<any> {
    // TEMP: For development
    if (environment.devMode.loadLocalInputTemplate) {
      return () =>
        import(
          /* webpackChunkName: "tunnel-/vlocal" */
          '../../shared/templates/tunnel/local-input-template'
        );
    }

    switch (version) {
      case TunnelTemplateVersion.V1_0_21:
      case TunnelTemplateVersion.V1_0_22:
      case TunnelTemplateVersion.V1_0_23:
      case TunnelTemplateVersion.V1_0_24:
      case TunnelTemplateVersion.V2_0_0:
      case TunnelTemplateVersion.V2_1_0:
      case TunnelTemplateVersion.V2_1_3:
      case TunnelTemplateVersion.V2_1_4:
      case TunnelTemplateVersion.V2_2_0:
      case TunnelTemplateVersion.V2_2_2:
      case TunnelTemplateVersion.V2_2_3:
      case TunnelTemplateVersion.V2_2_4:
      case TunnelTemplateVersion.V2_2_5:
      case TunnelTemplateVersion.V3_0_0:
      case TunnelTemplateVersion.V3_0_1:
      case TunnelTemplateVersion.V3_1_0:
      case TunnelTemplateVersion.V3_1_1:
      case TunnelTemplateVersion.V3_1_2:
      case TunnelTemplateVersion.V4_0_0:
      case TunnelTemplateVersion.V5_0_0:
      case TunnelTemplateVersion.V5_1_0:
        return () =>
          import(
            /* webpackChunkName: "tunnel-/v5.1.0" */
            '../../shared/templates/tunnel/template-archive/v5.1.0'
          );
    }
  }

  /**
   * Check if a version string matches a pattern string.
   * Supports wildcard matching using asterisk (*) for any part of the version.
   * Versions follow semantic versioning format (major.minor.patch).
   *
   * @param version - The version string to check (e.g., "7.1.0")
   * @param pattern - The pattern string to match against (e.g., "7.*.*", "7.1.*", or "7.1.0")
   * @returns True if the version matches the pattern, false otherwise
   */
  private isPatternMatch(version: string, pattern: string): boolean {
    // Split version and pattern by "." to get [major, minor, patch]
    const versionParts = version.split('.');
    const patternParts = pattern.split('.');

    // Both must have exactly 3 parts (major.minor.patch)
    if (patternParts.length !== 3) {
      console.warn(
        `Invalid pattern format: "${pattern}". Expected format: "major.minor.patch"`
      );

      return false;
    }

    // Compare each part
    for (let i = 0; i < 3; i++) {
      const versionPart = versionParts[i];
      const patternPart = patternParts[i];

      // If pattern part is "*", it matches any value
      if (patternPart === '*') continue;

      // Otherwise, perform exact string comparison
      if (versionPart !== patternPart) return false;
    }

    return true;
  }

  /**
   * Update the version metadata in the bridge template.
   * Sets the model version and generates output version strings.
   *
   * @param template - The template array containing the model
   * @param version - The version to apply to the template
   * @returns The template with updated version metadata
   */
  private updateBridgeTemplateVersion(
    template: any[],
    version: BridgeTemplateVersion
  ): any {
    const [{ model }] = template;
    model.version = version;
    model.outputVersions = {
      output2: `2.${version}`,
      output5: `5.${version}`,
      output6: `6.${version}`,
    };
    template[0] = { model };

    return template;
  }

  /**
   * Update the version metadata in the tunnel template.
   * Sets the model version and generates output version strings.
   *
   * @param template - The template array containing the model
   * @param version - The version to apply to the template
   * @returns The template with updated version metadata
   */
  private updateTunnelTemplateVersion(
    template: any[],
    version: TunnelTemplateVersion
  ): any {
    const [{ model }] = template;
    model.version = version;
    model.outputVersions = {
      output2: `2.${version}`,
      output5: `5.${version}`,
    };
    template[0] = { model };

    return template;
  }
}
